import { useEffect, useRef, useState } from 'react'
import {
  sizeBySpend,
  sizeByWorkload,
  type PublicSizeResponse,
  type SpendProvider,
} from '../lib/api'
import { pickFallbackBySpend, pickFallbackByVmCount } from '../lib/sizing-fallback'
import { SizerResultCard } from './SizerResultCard'
import { cn } from '../lib/cn'

interface Props {
  onJumpToCompare: () => void
}

type Term = 12 | 36 | 60
type Tab = 'spend' | 'workload'

const PROVIDER_OPTIONS: Array<{ value: SpendProvider; label: string }> = [
  { value: 'aws_govcloud', label: 'AWS GovCloud' },
  { value: 'azure_gov', label: 'Azure Government' },
  { value: 'gcc_high', label: 'GCC High' },
  { value: 'other_diy', label: 'Other / DIY' },
]

export function Sizer({ onJumpToCompare }: Props) {
  const [tab, setTab] = useState<Tab>('spend')
  const [termMonths, setTermMonths] = useState<Term>(36)

  const [provider, setProvider] = useState<SpendProvider>('aws_govcloud')
  const [monthlySpend, setMonthlySpend] = useState<number>(50_000)

  const [vmCount, setVmCount] = useState<number>(100)
  const [avgVcpu, setAvgVcpu] = useState<number>(4)
  const [avgRamGb, setAvgRamGb] = useState<number>(16)
  const [avgVmStorageGb, setAvgVmStorageGb] = useState<number>(100)

  const [result, setResult] = useState<PublicSizeResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [isFallback, setIsFallback] = useState(false)

  const debounceRef = useRef<number | null>(null)
  const requestIdRef = useRef(0)

  function triggerSize() {
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(async () => {
      const id = ++requestIdRef.current
      setLoading(true)
      try {
        let r: PublicSizeResponse
        if (tab === 'spend') {
          r = await sizeBySpend({
            currentMonthlySpend: monthlySpend,
            currentProvider: provider,
            termMonths,
          })
        } else {
          r = await sizeByWorkload({
            vmCount,
            avgVcpu,
            avgRamGb,
            avgVmStorageGb,
            termMonths,
          })
        }
        if (id !== requestIdRef.current) return
        setResult(r)
        setIsFallback(false)
      } catch {
        if (id !== requestIdRef.current) return
        const fallback =
          tab === 'spend' ? pickFallbackBySpend(monthlySpend) : pickFallbackByVmCount(vmCount)
        setResult({ ...fallback, pricing: { ...fallback.pricing, termMonths } })
        setIsFallback(true)
      } finally {
        if (id === requestIdRef.current) setLoading(false)
      }
    }, 400)
  }

  useEffect(() => {
    triggerSize()
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, termMonths, provider, monthlySpend, vmCount, avgVcpu, avgRamGb, avgVmStorageGb])

  return (
    <section id="sizer" className="bg-chalk py-20 lg:py-24 scroll-mt-16">
      <div className="mx-auto max-w-6xl px-6">
        <header className="mb-10 max-w-3xl">
          <h2 className="text-3xl sm:text-4xl font-semibold text-navy tracking-tight">
            Size your workload. See what it costs.
          </h2>
          <p className="mt-3 text-base text-navy/70">
            Live sizing against the CyCloud catalog. Adjust inputs and the recommendation
            updates immediately.
          </p>
        </header>

        <div className="bg-white border border-mist rounded-card shadow-card overflow-hidden">
          <div className="flex border-b border-mist">
            <TabButton active={tab === 'spend'} onClick={() => setTab('spend')}>
              Size by current spend
            </TabButton>
            <TabButton active={tab === 'workload'} onClick={() => setTab('workload')}>
              Size by workload
            </TabButton>
          </div>

          <div className="p-6 sm:p-8">
            {tab === 'spend' ? (
              <SpendInputs
                provider={provider}
                onProviderChange={setProvider}
                monthlySpend={monthlySpend}
                onMonthlySpendChange={setMonthlySpend}
                termMonths={termMonths}
                onTermChange={setTermMonths}
              />
            ) : (
              <WorkloadInputs
                vmCount={vmCount}
                onVmCountChange={setVmCount}
                avgVcpu={avgVcpu}
                onAvgVcpuChange={setAvgVcpu}
                avgRamGb={avgRamGb}
                onAvgRamGbChange={setAvgRamGb}
                avgVmStorageGb={avgVmStorageGb}
                onAvgVmStorageGbChange={setAvgVmStorageGb}
                termMonths={termMonths}
                onTermChange={setTermMonths}
              />
            )}
          </div>
        </div>

        <div className="mt-6 relative">
          {loading && (
            <div className="absolute top-3 right-3 text-xs font-data text-navy/50 animate-pulse-mint">
              sizing…
            </div>
          )}
          {result && (
            <SizerResultCard
              result={result}
              termMonths={termMonths}
              isFallback={isFallback}
              onJumpToCompare={onJumpToCompare}
            />
          )}
        </div>
      </div>
    </section>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-5 sm:px-6 py-4 text-sm font-medium transition-colors border-b-2',
        active
          ? 'border-mint text-navy bg-foam/30'
          : 'border-transparent text-navy/60 hover:text-navy hover:bg-mist/40'
      )}
    >
      {children}
    </button>
  )
}

function TermSelector({
  value,
  onChange,
}: {
  value: Term
  onChange: (t: Term) => void
}) {
  return (
    <div>
      <label className="text-xs font-data uppercase tracking-wide text-navy/50">
        Contract term
      </label>
      <div className="mt-2 flex gap-2">
        {[12, 36, 60].map((t) => (
          <button
            key={t}
            onClick={() => onChange(t as Term)}
            className={cn(
              'flex-1 px-4 py-2 rounded font-data text-sm border transition-colors',
              value === t
                ? 'border-mint bg-foam text-navy font-semibold'
                : 'border-mist bg-white text-navy/70 hover:border-mint/50'
            )}
          >
            {t} mo
          </button>
        ))}
      </div>
    </div>
  )
}

function SpendInputs({
  provider,
  onProviderChange,
  monthlySpend,
  onMonthlySpendChange,
  termMonths,
  onTermChange,
}: {
  provider: SpendProvider
  onProviderChange: (p: SpendProvider) => void
  monthlySpend: number
  onMonthlySpendChange: (n: number) => void
  termMonths: Term
  onTermChange: (t: Term) => void
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:col-span-1">
        <label className="text-xs font-data uppercase tracking-wide text-navy/50">
          Current provider
        </label>
        <select
          value={provider}
          onChange={(e) => onProviderChange(e.target.value as SpendProvider)}
          className="mt-2 w-full border border-mist rounded px-3 py-2 bg-white text-navy font-data text-sm focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint"
        >
          {PROVIDER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className="md:col-span-1">
        <label className="text-xs font-data uppercase tracking-wide text-navy/50">
          Current monthly spend
        </label>
        <div className="mt-2 relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-data text-navy/50 text-sm">
            $
          </span>
          <input
            type="number"
            min={1000}
            max={1_000_000}
            step={500}
            value={monthlySpend}
            onChange={(e) => {
              const v = Number(e.target.value)
              if (Number.isFinite(v)) onMonthlySpendChange(Math.max(1000, Math.min(1_000_000, v)))
            }}
            className="w-full border border-mist rounded pl-7 pr-3 py-2 bg-white text-navy font-data text-sm focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint"
          />
        </div>
        <div className="mt-1 text-xs text-navy/50 font-data">
          ${(monthlySpend * 12).toLocaleString()} annualized
        </div>
      </div>
      <div className="md:col-span-1">
        <TermSelector value={termMonths} onChange={onTermChange} />
      </div>
    </div>
  )
}

function SliderRow({
  label,
  unit,
  min,
  max,
  step,
  value,
  onChange,
}: {
  label: string
  unit: string
  min: number
  max: number
  step: number
  value: number
  onChange: (n: number) => void
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-data uppercase tracking-wide text-navy/50">{label}</label>
        <span className="font-data text-sm text-navy font-medium">
          {value.toLocaleString()} {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 w-full"
      />
    </div>
  )
}

function WorkloadInputs({
  vmCount,
  onVmCountChange,
  avgVcpu,
  onAvgVcpuChange,
  avgRamGb,
  onAvgRamGbChange,
  avgVmStorageGb,
  onAvgVmStorageGbChange,
  termMonths,
  onTermChange,
}: {
  vmCount: number
  onVmCountChange: (n: number) => void
  avgVcpu: number
  onAvgVcpuChange: (n: number) => void
  avgRamGb: number
  onAvgRamGbChange: (n: number) => void
  avgVmStorageGb: number
  onAvgVmStorageGbChange: (n: number) => void
  termMonths: Term
  onTermChange: (t: Term) => void
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SliderRow
          label="VM count"
          unit="VMs"
          min={1}
          max={2000}
          step={1}
          value={vmCount}
          onChange={onVmCountChange}
        />
        <SliderRow
          label="Avg vCPU per VM"
          unit="vCPU"
          min={1}
          max={64}
          step={1}
          value={avgVcpu}
          onChange={onAvgVcpuChange}
        />
        <SliderRow
          label="Avg RAM per VM"
          unit="GB"
          min={1}
          max={512}
          step={1}
          value={avgRamGb}
          onChange={onAvgRamGbChange}
        />
        <SliderRow
          label="Avg storage per VM"
          unit="GB"
          min={10}
          max={5000}
          step={10}
          value={avgVmStorageGb}
          onChange={onAvgVmStorageGbChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 grid grid-cols-3 gap-3 text-center">
          <Aggregate label="Total vCPU" value={(vmCount * avgVcpu).toLocaleString()} />
          <Aggregate label="Total RAM" value={`${(vmCount * avgRamGb).toLocaleString()} GB`} />
          <Aggregate
            label="Total storage"
            value={`${((vmCount * avgVmStorageGb) / 1024).toFixed(1)} TB`}
          />
        </div>
        <TermSelector value={termMonths} onChange={onTermChange} />
      </div>
    </div>
  )
}

function Aggregate({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-chalk border border-mist rounded px-3 py-2">
      <div className="text-[10px] font-data uppercase tracking-wide text-navy/55">{label}</div>
      <div className="font-data text-sm text-navy mt-0.5">{value}</div>
    </div>
  )
}
